// Team absence calendar (proto CgCalendarView) — one row per employee,
// one column per day, absence pills + a coverage row. Data comes from
// GET /monthly-grid, which is per-entity: with the "Toutes" filter the
// view asks the user to pick an entity (same rule as Grille mensuelle).
import { useState } from 'react';
import { useMonthlyGrid, type MonthCellDTO, type MonthCellType } from '../../api/hooks.js';
import { cgAvColor, cgFmtLong, cgInitials, cgNum, cgToday, matchesQuery, ymLabel, CG_DOW } from './common.js';

interface PillType { lbl: string; short: string; c: string; bg: string }
const PILL_TYPES: Partial<Record<MonthCellType, PillType>> = {
  c: { lbl: 'Congé annuel', short: 'Congé', c: 'var(--shfc)', bg: 'var(--shfs)' },
  recup: { lbl: 'Récupération', short: 'Récup', c: 'var(--infoc)', bg: 'var(--infos)' },
  m: { lbl: 'Arrêt maladie', short: 'Maladie', c: 'var(--warnc)', bg: 'var(--warns)' },
  aj: { lbl: 'Absence justifiée', short: 'Abs. J', c: 'var(--infoc)', bg: 'var(--infos)' },
  anj: { lbl: 'Absence injustifiée', short: 'Abs.', c: 'var(--badc)', bg: 'var(--bads)' },
};

export function LeaveCalendar({ entityId, month, types, q, balMap, onOpen, onNew }: {
  entityId: string;
  month: string; // YYYY-MM
  types: MonthCellType[]; // which absence types this page displays
  q: string;
  balMap: Map<string, number>; // employeeId → balance shown next to the name
  onOpen: (empId: string) => void;
  onNew: (empId: string, date: string) => void;
}) {
  const [hover, setHover] = useState<{ emp: string; lbl: string; date: string; x: number; y: number } | null>(null);
  const [y, m] = month.split('-').map(Number);
  const grid = useMonthlyGrid({ entityId, year: y!, month: m!, enabled: entityId !== 'all' });

  if (entityId === 'all') {
    return (
      <div className="cg-placeholder view-enter">
        Le calendrier d'équipe s'affiche par entité — choisissez SHF, OCT, SGA ou SGN dans la barre du haut.
      </div>
    );
  }
  if (grid.isLoading) return <div className="cg-placeholder view-enter">Chargement du calendrier…</div>;
  if (grid.isError || !grid.data) return <div className="cg-placeholder view-enter">Impossible de charger le calendrier — réessayez.</div>;

  const data = grid.data;
  const days = data.days;
  const nDays = days.length;
  const today = cgToday();
  const rows = data.rows.filter((r) =>
    matchesQuery({ name: r.employee.name, role: r.employee.role, code: r.employee.code }, q));

  // Displayed absence type of a cell: prefer the paie classification,
  // fall back to the réelle one (pre-arbitration).
  const cellType = (cell: MonthCellDTO): MonthCellType | null => {
    if (types.includes(cell.typePaie)) return cell.typePaie;
    if (types.includes(cell.typeReal)) return cell.typeReal;
    return null;
  };

  const cover = days.map((d, i) =>
    rows.filter((r) => !d.isWeekend && !d.isFerie && cellType(r.cells[i]!)).length);
  const maxCover = Math.max(...cover, 1);
  const seuil = Math.max(2, Math.ceil(rows.length * 0.25));
  const tension = days.map((d, i) => ({ d: d.date, n: cover[i]! })).filter((x) => x.n >= seuil);
  const todayIdx = days.findIndex((d) => d.date === today);

  return (
    <div className="view-enter">
      <div className="cg-callegend">
        <div className="cg-legs">
          {types.map((t) => {
            const T = PILL_TYPES[t];
            return T ? <span key={t} className="cg-leg"><i style={{ background: T.bg, borderColor: T.c }} />{T.lbl}</span> : null;
          })}
          <span className="cg-leg"><i className="hatch" />Week-end / férié</span>
        </div>
        <div className="cg-calstat">
          <b className="gm-mono">{todayIdx >= 0 ? cover[todayIdx] : Math.max(...cover, 0)}</b>
          <span>{todayIdx >= 0 ? "absents aujourd'hui" : "pic d'absences"}</span>
        </div>
      </div>
      {tension.length > 0 && (
        <div className="cg-tension">
          <span className="cg-tension-ic">⚠</span>
          <span>
            <b>{tension.length} journée{tension.length > 1 ? 's' : ''} sous tension</b> — {seuil} absents ou plus :{' '}
            {tension.slice(0, 6).map((t) => `${Number(t.d.slice(8, 10))}`).join(' · ')}{tension.length > 6 ? ' …' : ''}
          </span>
        </div>
      )}
      <div className="gm-gridwrap">
        <div className="cg-calscroll">
          <div className="cg-cal" style={{ '--n': nDays } as React.CSSProperties}>
            <div className="cg-cal-head">
              <div className="cg-cal-corner">{ymLabel(month)}</div>
              {days.map((d) => {
                const off = d.isWeekend || d.isFerie;
                return (
                  <div key={d.date} className={'cg-dh' + (off ? ' off' : '') + (d.isFerie ? ' hol' : '') + (d.date === today ? ' today' : '')}>
                    <span className="cg-dh-w">{CG_DOW[d.dow]}</span>
                    <span className="cg-dh-n">{d.day}</span>
                  </div>
                );
              })}
            </div>
            {rows.map((r) => (
              <div className="cg-cal-row" key={r.employee.id}>
                <div className="cg-cal-name" onClick={() => onOpen(r.employee.id)}>
                  <span className="gm-av sm" style={{ background: cgAvColor(r.employee.id) }}>{cgInitials(r.employee.name)}</span>
                  <span className="cg-cal-nm">{r.employee.name}</span>
                  <span className="cg-cal-bal gm-mono">{cgNum(balMap.get(r.employee.id) ?? 0)}</span>
                </div>
                {r.cells.map((cell, i) => {
                  const d = days[i]!;
                  const off = d.isWeekend || d.isFerie;
                  const t = cellType(cell);
                  const T = t ? PILL_TYPES[t] : null;
                  const prev = i > 0 ? cellType(r.cells[i - 1]!) : null;
                  const next = i < nDays - 1 ? cellType(r.cells[i + 1]!) : null;
                  const start = !!t && (prev !== t || i === 0);
                  const end = !!t && (next !== t || i === nDays - 1);
                  return (
                    <div key={cell.date}
                      className={'cg-cell' + (off ? ' off' : '') + (cell.date === today ? ' today' : '')}
                      onClick={() => { if (!off && !t) onNew(r.employee.id, cell.date); }}
                      onMouseEnter={(ev) => {
                        if (!T) return;
                        const rect = ev.currentTarget.getBoundingClientRect();
                        setHover({ emp: r.employee.name, lbl: T.lbl, date: cell.date, x: rect.left, y: rect.top });
                      }}
                      onMouseLeave={() => setHover(null)}>
                      {T && (
                        <span className={'cg-pill' + (start ? ' s' : '') + (end ? ' e' : '')}
                          style={{ background: T.bg, color: T.c, borderColor: T.c }}>
                          {start && !off ? T.short : ''}
                        </span>
                      )}
                      {!T && !off && <span className="cg-plus">+</span>}
                    </div>
                  );
                })}
              </div>
            ))}
            {rows.length === 0 && (
              <div className="cg-empty" style={{ margin: 14 }}>Aucun employé pour ce filtre.</div>
            )}
            <div className="cg-cal-row cover">
              <div className="cg-cal-name cov">Couverture · absents / jour</div>
              {days.map((d, i) => {
                const off = d.isWeekend || d.isFerie;
                const n = cover[i]!;
                return (
                  <div key={d.date} className={'cg-cov' + (off ? ' off' : '')}>
                    {!off && (
                      <span className={'cg-covbar' + (n >= seuil ? ' hot' : '')}
                        style={{ height: `${Math.max(n ? 12 : 2, (n / maxCover) * 100)}%` }}
                        title={`${n} absent${n > 1 ? 's' : ''} le ${cgFmtLong(d.date)}`} />
                    )}
                    {!off && n > 0 && <span className="cg-covn gm-mono">{n}</span>}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
      {hover && (
        <div className="cg-tip" style={{ left: Math.min(hover.x, window.innerWidth - 280), top: hover.y - 74 }}>
          <b>{hover.emp}</b>
          <span>{hover.lbl}</span>
          <span className="dim">{cgFmtLong(hover.date)}</span>
        </div>
      )}
    </div>
  );
}
